import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import AutorenewIcon from '@mui/icons-material/Autorenew';

import {
  Box,
  Button,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import useMenuItems from '@/features/portfolio-analysis/hooks/useMenuItems';
import useSearchParams from '@/hooks/useStableSearchParams';

export default function AppBar({
  excludedAnalysisTypes = [],
  outOfSync = true,
}) {
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const anchorRefs = useRef([]);

  const menuItems = useMenuItems();

  const handleClose = (event, index) => {
    if (anchorRefs.current[index]?.contains(event.target)) return;
    setOpenMenuIndex(null);
  };

  const hoverTimers = useRef({}); // index -> timeout ID

  const handleMouseEnter = (index) => {
    clearTimeout(hoverTimers.current[index]);
    hoverTimers.current[index] = setTimeout(() => {
      setOpenMenuIndex(index);
    }, 300);
  };

  const handleMouseLeave = (index) => {
    clearTimeout(hoverTimers.current[index]);
    hoverTimers.current[index] = setTimeout(() => {
      setOpenMenuIndex(null);
    }, 200); // optionally delay closing
  };
  const [searchParams] = useSearchParams();
  const handleMenuClick = (path) => {
    //Aka overview
    if (path === '') {
      navigate(`/portfolio-analysis-hub?${searchParams.toString()}`);
    }
    navigate(`/portfolio-analysis-hub/${path}?${searchParams.toString()}`);
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        px: 2,
        py: 1,
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',

          alignItems: 'center',
          gap: 1,
        }}
      >
        {menuItems.map((item, index) => {
          const hasMenu = Boolean(item.submenu);
          const open = openMenuIndex === index;
          const isDisabled = excludedAnalysisTypes.some((type) =>
            item.path.includes(type),
          );
          const isSelected =
            item.path === ''
              ? location.pathname.replace(/[/#]/g, '') ===
                'portfolio-analysis-hub'
              : location.pathname.includes(item.path.split('/')[0]) ||
                (item.submenu &&
                  item.submenu.some((subItem) =>
                    location.pathname.includes(subItem.path),
                  ));

          return (
            <Box key={item.label} sx={{ position: 'relative' }}>
              <Button
                disabled={isDisabled}
                ref={(el) => (anchorRefs.current[index] = el)}
                onClick={() => handleMenuClick(item.path)}
                onMouseEnter={() => hasMenu && handleMouseEnter(index)}
                onMouseLeave={() => hasMenu && handleMouseLeave(index)}
                startIcon={item.icon || null}
                endIcon={
                  hasMenu ? (
                    <Box
                      component="span"
                      sx={{
                        display: 'flex',
                        transition: 'transform 0.2s ease-in-out',
                        transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                      }}
                    >
                      <KeyboardArrowRightRoundedIcon fontSize="small" />
                    </Box>
                  ) : null
                }
                sx={{
                  textTransform: 'none',
                  color: isSelected ? 'text.primary' : 'text.secondary',
                  fontSize: '0.9rem',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: isSelected ? 'action.hover' : 'transparent',
                  '&:hover': {
                    bgcolor: isSelected ? 'action.hover' : 'action.hover',
                    color: 'text.primary',
                  },
                }}
              >
                {item.submenu &&
                !isDisabled &&
                item.submenu.some((subItem) =>
                  location.pathname.includes(subItem.path),
                )
                  ? item.submenu.find((subItem) =>
                      location.pathname.includes(subItem.path),
                    )?.label
                  : item.label}
              </Button>

              {hasMenu && (
                <Popper
                  open={open}
                  anchorEl={anchorRefs.current[index]}
                  placement="bottom-end"
                  transition
                  disablePortal
                  sx={{ zIndex: 1301 }}
                  onMouseEnter={() => handleMouseEnter(index)}
                  onMouseLeave={() => handleMouseLeave(index)}
                >
                  {({ TransitionProps }) => (
                    <Grow
                      {...TransitionProps}
                      style={{ transformOrigin: 'left top' }}
                    >
                      <Paper
                        elevation={0}
                        variant="outlined"
                        sx={{ mt: 0.5, borderRadius: 2 }}
                      >
                        <ClickAwayListener
                          onClickAway={(event) => handleClose(event, index)}
                        >
                          <MenuList
                            autoFocusItem={open}
                            dense
                            sx={{ minWidth: 180, p: 1 }}
                          >
                            {item.submenu.map((subItem) => {
                              const isSubSelected = location.pathname.includes(
                                subItem.path,
                              );

                              return (
                                <MenuItem
                                  key={subItem.label}
                                  onClick={() => {
                                    setOpenMenuIndex(null); // Close the popper
                                    handleMenuClick(subItem.path);
                                  }}
                                  sx={{
                                    py: '6px',
                                    px: '12px',
                                    borderRadius: '5px',
                                    bgcolor: isSubSelected
                                      ? 'action.hover'
                                      : 'transparent',
                                    '&:hover': {
                                      // bgcolor: isSubSelected ? 'hsla(67, 100%, 40%, 0.2)' : 'action.hover',
                                      bgcolor: isSubSelected
                                        ? 'action.hover'
                                        : 'action.hover',
                                      transition: 'all 0.3s ease',
                                      color: 'inherit',
                                    },
                                  }}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    {subItem.icon && subItem.icon}
                                    <Typography
                                      variant="body2"
                                      fontWeight="medium"
                                    >
                                      {subItem.label}
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              )}
            </Box>
          );
        })}
      </Box>
      <Box
        sx={{
          display: 'flex',
          marginLeft: 'auto',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {' '}
        {outOfSync && (
          <Tooltip
            title={'Analysen er ude af sync og er i gang med at opdatere'}
            placement="top"
          >
            <AutorenewIcon />
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
}
